import React, { useState } from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import Navbar from '../../../shared/components/Navbar';
import Footer from '../../../shared/components/Footer';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import Card from '../../../shared/components/Card';

const FAQ: React.FC = () => {
  const { t } = usePageTranslation('public', 'FAQ');
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev =>
      prev.includes(index)
        ? prev.filter(item => item !== index)
        : [...prev, index]
    );
  };

  const faqData = [
    {
      category: t('categories.general'),
      questions: [
        {
          question: t('questions.whatIs.question'),
          answer: t('questions.whatIs.answer')
        },
        {
          question: t('questions.invitation.question'),
          answer: t('questions.invitation.answer')
        },
        {
          question: t('questions.free.question'),
          answer: t('questions.free.answer')
        }
      ]
    },
    {
      category: t('categories.account'),
      questions: [
        {
          question: t('questions.prerequisites.question'),
          answer: t('questions.prerequisites.answer')
        },
        {
          question: t('questions.department.question'),
          answer: t('questions.department.answer')
        },
        {
          question: t('questions.password.question'),
          answer: t('questions.password.answer')
        }
      ]
    },
    {
      category: t('categories.gameplay'),
      questions: [
        {
          question: t('questions.recruit.question'),
          answer: t('questions.recruit.answer')
        },
        {
          question: t('questions.build.question'),
          answer: t('questions.build.answer')
        },
        {
          question: t('questions.interventions.question'),
          answer: t('questions.interventions.answer')
        }
      ]
    },
    {
      category: t('categories.technical'),
      questions: [
        {
          question: t('questions.loading.question'),
          answer: t('questions.loading.answer')
        },
        {
          question: t('questions.notifications.question'),
          answer: t('questions.notifications.answer')
        },
        {
          question: t('questions.slow.question'),
          answer: t('questions.slow.answer')
        }
      ]
    },
    {
      category: t('categories.community'),
      questions: [
        {
          question: t('questions.contact.question'),
          answer: t('questions.contact.answer')
        },
        {
          question: t('questions.discuss.question'),
          answer: t('questions.discuss.answer')
        },
        {
          question: t('questions.bug.question'),
          answer: t('questions.bug.answer')
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen relative">
      <BackgroundImage />
      <Navbar />
      <div className="relative z-10">
        {/* Header de la page */}
        <section className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 text-gradient">
                {t('title')}
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                {t('subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Contenu principal */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="p-8 md:p-12">
              <div className="space-y-8">
                {faqData.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="category-section">
                    <h2 className="text-3xl font-bold text-white mb-6 flex items-center">
                      <span className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-blue-500/50">
                        {categoryIndex + 1}
                      </span>
                      {category.category}
                    </h2>

                    <div className="space-y-4">
                      {category.questions.map((item, questionIndex) => {
                        const globalIndex = categoryIndex * 10 + questionIndex;
                        const isOpen = openItems.includes(globalIndex);

                        return (
                          <div key={questionIndex} className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800/50">
                            <button
                              onClick={() => toggleItem(globalIndex)}
                              className="w-full px-6 py-4 text-left bg-gray-800/50 hover:bg-gray-700/50 transition-colors duration-200 flex items-center justify-between"
                            >
                              <span className="font-semibold text-white">{item.question}</span>
                              <span className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </span>
                            </button>

                            {isOpen && (
                              <div className="px-6 py-4 bg-gray-900/50 border-t border-gray-700">
                                <p className="text-gray-300 leading-relaxed">{item.answer}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Section Contact */}
              <div className="mt-12 p-6 bg-gradient-to-r from-gray-800/50 to-blue-900/50 rounded-xl border-l-4 border-blue-500">
                <h3 className="text-xl font-semibold text-white mb-4">{t('notFound.title')}</h3>
                <p className="text-gray-300 mb-4">
                  {t('notFound.description')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://discord.gg/7wVa8EkvnP"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                    {t('notFound.discord')}
                  </a>
                  <a
                    href="mailto:support@opsecours.fr"
                    className="inline-flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {t('notFound.email')}
                  </a>
                </div>
              </div>
            </Card>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default FAQ;

